import { z } from 'zod';
import { tipoDeCierreSchema } from './eventos.js';
import { filaDeGrupoSchema } from './formularios/tipos.js';
import { conjuntoMinimoSchema, problemaDeLaEdad } from './identidad.js';
import { patronDeCadenciaSchema } from './programa/cadencia.js';
/**
 * El alta del paciente (E3.24, ADR-020): el contrato entre la pantalla y la API.
 *
 * Tres pasos y una decisión en el medio. **Paso 1**, la identidad: el Conjunto
 * Mínimo de Datos y el celular. Con eso la API busca candidatos en la
 * institución y coincidencias en la red; si hay, quien da el alta decide antes
 * de seguir. **Paso 2**, el episodio: fractura, cirugía, internación y el día 0.
 * **Paso 3**, el plan: el del protocolo, con sus ajustes. La API recibe los tres
 * juntos al confirmar, vuelve a buscar coincidencias y exige que cada una tenga
 * su decisión: una pantalla vieja no puede saltearse un candidato nuevo.
 *
 * Los catálogos de fractura y cirugía viven en `@pasos/fhir` (RFC, ADR-009) y
 * este paquete no depende de él: acá viajan como texto y la API los valida
 * contra el catálogo. La pantalla los pide a la API.
 */
// ── Paso 2 · el episodio ───────────────────────────────────────────────────
export const MENSAJE_FECHAS_DEL_EPISODIO = 'Revisá las fechas: la cirugía no puede ser posterior al egreso, ni la internación posterior a la cirugía.';
export const episodioDelAltaSchema = z
    .strictObject({
    /** Tipo AO/OTA del RFC: `cuello`, `trocanterica`… */
    fracturaTipo: z.string().min(1).max(40),
    /** Grupo AO/OTA. Ausente en `otras-combinadas`. */
    fracturaGrupo: z.string().min(1).max(10).optional(),
    /** Código de cirugía del RFC (campo 5.09). */
    cirugia: z.string().min(1).max(40),
    cirugiaAt: z.iso.date(),
    /** Ingreso a la internación, si se conoce. */
    internacionDesde: z.iso.date().optional(),
    /** Egreso real: **el día 0 del programa**. */
    egresoAt: z.iso.date(),
    profesionalResponsableId: z.uuid(),
    /**
     * La medicación con la que se fue a su casa (E3.25): bloque **opcional**,
     * con el mismo grupo del catálogo que los cuestionarios de hito (droga,
     * potencia en mg, intervalo en horas). Es la base de la comparación de
     * D+360. La forma de cada fila la valida la API contra el formulario
     * `medicacion-al-egreso` y el catálogo cargado; acá, que sean filas.
     */
    medicacionAlEgreso: z.array(filaDeGrupoSchema).min(1).max(15).optional(),
})
    .refine((episodio) => episodio.cirugiaAt <= episodio.egresoAt &&
    (!episodio.internacionDesde || episodio.internacionDesde <= episodio.cirugiaAt), { message: MENSAJE_FECHAS_DEL_EPISODIO, path: ['egresoAt'] });
// ── Paso 3 · el plan ───────────────────────────────────────────────────────
/**
 * El plan base del protocolo con los dos ajustes que ya existen (UC-M01,
 * ADR-005): la cadencia de los check-ins y la exclusión de las teleconsultas
 * opcionales, cada una con su motivo.
 */
export const planDelAltaSchema = z.strictObject({
    patron: patronDeCadenciaSchema,
    exclusiones: z
        .array(z.strictObject({
        codigo: z.string().min(1).max(40),
        motivo: z.string().min(3).max(200),
    }))
        .max(4),
});
// ── Las decisiones ─────────────────────────────────────────────────────────
export const decisionDeIdentidadSchema = z.enum(['misma_persona', 'otra_persona']);
export const resolucionDeCandidatoSchema = z.strictObject({
    candidatoUsuarioId: z.uuid(),
    decision: decisionDeIdentidadSchema,
});
// ── Los pedidos ────────────────────────────────────────────────────────────
/** Paso 1 · buscar candidatos y coincidencias en la red. */
export function busquedaDeIdentidadSchema(alDia) {
    return z.strictObject({ identidad: conjuntoMinimoSchema(alDia) });
}
/**
 * La confirmación: los tres pasos y las decisiones.
 *
 * `alDia` es el día del alta, lo único que se conoce en el paso 1. **La edad que
 * decide es la del egreso** (respuesta 2.9 de Fundación): una persona que hoy
 * tiene 60 pero egresó con 59 no entra, y por eso se vuelve a mirar acá, con el
 * egreso a la vista.
 */
export function solicitudDeAltaSchema(alDia) {
    return z
        .strictObject({
        identidad: conjuntoMinimoSchema(alDia),
        episodio: episodioDelAltaSchema,
        plan: planDelAltaSchema,
        /** Una por candidato de la institución, si hubo. */
        resoluciones: z.array(resolucionDeCandidatoSchema).max(20),
        /** Si hubo coincidencia en la red, qué decidió quien da el alta. */
        resolucionRed: decisionDeIdentidadSchema.optional(),
    })
        .superRefine(({ identidad, episodio }, ctx) => {
        const problema = problemaDeLaEdad(identidad.fechaNacimiento, episodio.egresoAt);
        if (problema) {
            ctx.addIssue({ code: 'custom', path: ['identidad', 'fechaNacimiento'], message: problema });
        }
    });
}
// ── Las respuestas ─────────────────────────────────────────────────────────
/** Lo que se descifra de un candidato, sólo para quien da el alta. */
export const identidadVisibleSchema = z.strictObject({
    nombres: z.string(),
    apellidos: z.string(),
    tipoDocumento: z.string(),
    numeroDocumento: z.string(),
    fechaNacimiento: z.iso.date(),
});
export const candidatoDelAltaSchema = z.strictObject({
    usuarioId: z.uuid(),
    puntaje: z.number().int().min(0).max(100),
    /** Qué coincidió: `documento`, `fecha_nacimiento`, `apellido_fonetico`… */
    coincidencias: z.array(z.string()),
    identidad: identidadVisibleSchema,
    programas: z.array(z.strictObject({
        caseId: z.string(),
        estado: z.enum(['ACTIVO', 'CERRADO']),
        iniciadoAt: z.iso.datetime(),
        cerradoAt: z.iso.datetime().nullable(),
        /** Con qué tipo cerró (CIERRES-1): decide si el alta es un reingreso. */
        tipoDeCierre: tipoDeCierreSchema.nullable(),
    })),
});
/**
 * El aviso de la red (ADR-020, E2.5): la institución, las fechas y el estado del
 * programa anterior. **Nada más de la otra institución.**
 */
export const avisoDeRedSchema = z.strictObject({
    institucion: z.string(),
    inicio: z.iso.datetime(),
    fin: z.iso.datetime().nullable(),
    estado: z.enum(['activo', 'cerrado']),
    tipoDeCierre: z.string().nullable(),
});
export const coincidenciasDelAltaSchema = z.strictObject({
    candidatos: z.array(candidatoDelAltaSchema),
    red: z.array(avisoDeRedSchema),
});
export const altaCreadaSchema = z.strictObject({
    programaId: z.uuid(),
    caseId: z.string(),
    pacienteUsuarioId: z.uuid(),
    /** `true` si el programa nuevo se vinculó a un paciente que ya existía. */
    vinculado: z.boolean(),
    enlace: z.strictObject({
        token: z.string(),
        tipo: z.literal('ONBOARDING_PACIENTE'),
        venceAt: z.iso.datetime(),
    }),
});
/** Lo que la pantalla necesita para armar los selectores del paso 2. */
export const catalogosDelAltaSchema = z.strictObject({
    fracturas: z.array(z.strictObject({
        codigo: z.string(),
        nombre: z.string(),
        grupos: z.array(z.strictObject({ codigo: z.string(), nombre: z.string() })),
    })),
    cirugias: z.array(z.strictObject({ codigo: z.string(), nombre: z.string() })),
    profesionales: z.array(z.strictObject({ id: z.uuid(), alias: z.string() })),
    /** El día del alta en la zona de la institución: contra él se valida la edad. */
    hoy: z.iso.date(),
});
//# sourceMappingURL=alta.js.map